import React, { useCallback, useMemo } from 'react';
import { Button } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SnackbarKey, useSnackbar } from 'notistack';
import { UserNoteGet } from 'api/models/UserNoteGet';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { AttioNotesService } from 'dashboard409a/services/attio/AttioNotesService';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import { Note } from 'dashboard409a/services/models.types';

export interface UseNoteHandlerProps {
  onSuccess?: (id: string | number, noteContent: string, createdNote?: UserNoteGet) => void;
}

export const useNoteHandler = ({ onSuccess }: UseNoteHandlerProps = {}) => {
  const notesService = useMemo(() => new AttioNotesService(), []);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const confirmAppendByCreatingNewNote = useCallback((): Promise<boolean> =>
    new Promise(resolve => {
      let resolved = false;

      enqueueSnackbar(
        'This note was created by another user. Saving will create a new note for you instead of updating the existing one.',
        {
          variant: 'warning',
          persist: true,
          action: (key: SnackbarKey) =>
            React.createElement(
              'span',
              null,
              React.createElement(
                Button,
                {
                  size: 'small',
                  onClick: () => {
                    if (resolved) {
                      return;
                    }
                    resolved = true;
                    closeSnackbar(key);
                    resolve(true);
                  },
                },
                'Create new note'
              ),
              React.createElement(
                Button,
                {
                  size: 'small',
                  onClick: () => {
                    if (resolved) {
                      return;
                    }
                    resolved = true;
                    closeSnackbar(key);
                    resolve(false);
                  },
                },
                'Cancel'
              )
            ),
        }
      );
    }), [closeSnackbar, enqueueSnackbar]);

  const noteMutation = useMutation({
    mutationFn: async ({
      request,
      hasExistingNote,
      existingNoteId,
    }: {
      request: CreateNoteRequest;
      hasExistingNote: boolean;
      existingNoteId?: string;
    }) => notesService.createOrUpdateNote(request, hasExistingNote, existingNoteId),
    onSuccess: (result: UserNoteGet, variables) => {
      if (variables.request.attio_valuation_id && !variables.hasExistingNote && result.attio_valuation_id) {
        const valuationId = result.attio_valuation_id;

        const note: Note = {
          id: result.id || 0,
          content: result.content || '',
          author_id: result.author || 0,
          author_email: '',
          author_name: '',
          created_at: result.created_at || new Date().toISOString(),
          updated_at: result.updated_at || new Date().toISOString(),
        };

        queryClient.setQueryData([AttioQueryKeys.BULK_NOTES, valuationId], note);

        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.BULK_NOTES],
        });
      }
    },
  });

  const handleNoteSave = useCallback(
    async (
      id: string | number,
      noteContent: string,
      currentNote: string | undefined,
      request: CreateNoteRequest,
      hasExistingNote: boolean,
      existingNoteId?: string
    ) => {
      try {
        const result = await noteMutation.mutateAsync({
          request,
          hasExistingNote,
          existingNoteId,
        });

        onSuccess?.(id, noteContent, result);

        enqueueSnackbar('Note saved successfully!', { variant: 'success' });
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'response' in err) {
          const errorResponse = err as {
            response?: { status?: number; data?: { message?: string; code?: string } };
          };

          const status = errorResponse.response?.status;
          const code = errorResponse.response?.data?.code;

          if (status === 403 && code === 'NOTE_NOT_AUTHOR') {
            const proceed = await confirmAppendByCreatingNewNote();
            if (!proceed) {
              enqueueSnackbar('Save cancelled.', { variant: 'info' });
              return;
            }

            const created = await noteMutation.mutateAsync({
              request,
              hasExistingNote: false,
              existingNoteId: undefined,
            });

            onSuccess?.(id, noteContent, created);

            enqueueSnackbar('Note created successfully!', { variant: 'success' });
            return;
          }
        }

        let errorMessage = 'Failed to save note. Please try again.';
        if (err && typeof err === 'object' && 'response' in err) {
          const errorResponse = err as { response?: { data?: { message?: string } } };
          if (errorResponse.response?.data?.message) {
            errorMessage = errorResponse.response.data.message;
          }
        } else if (err && typeof err === 'object' && 'message' in err) {
          const errorWithMessage = err as { message: string };
          errorMessage = errorWithMessage.message;
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
    [confirmAppendByCreatingNewNote, enqueueSnackbar, noteMutation, onSuccess]
  );

  return {
    isNoteLoading: noteMutation.isPending,
    handleNoteSave,
  };
};
